import { useCallback, useEffect, useReducer, useState } from 'react';
import { MutatorCallback } from 'swr';
import useSWRInfinite from 'swr/infinite';

import useCache from '../../../../Components/Context/Hooks/useCache';
import { InvoicesList } from '../../../../Types/api';
import { ListInvoice, MATCHED_STATUS } from '../../../../Types/invoice';

export const PAGE_SIZE = 10;

// We fetch 2 pages, so if we fetched 2 full pages we can't know for sure if there's more.
export const MAX_RELIABLE_INVOICE_COUNT = PAGE_SIZE * 2;

interface State {
  loading: boolean;
  data?: ListInvoice[];
  totalAmount: number;
}

export interface useInvoicesListResult extends State {
  error: any | null;
  loadMore: () => void;
  hasMore: boolean;
  mutate: (data?: MutatorCallback<InvoicesList[]>) => Promise<InvoicesList[] | undefined>;
}

type Action =
  | { type: 'loading'; payload?: undefined }
  | { type: 'invoices_loaded'; payload: { data: InvoicesList[]; actualPage: number } };

const reducer = (state: State, { type, payload }: Action): State => {
  switch (type) {
    case 'loading':
      return { ...state, loading: true };
    case 'invoices_loaded':
      return {
        loading: false,
        data: payload.data.slice(0, payload.actualPage).reduce<ListInvoice[]>((all, cur) => {
          all.push(...cur.data);
          return all;
        }, []),
        totalAmount: payload.data.reduce((sum, cur) => sum + cur.data.length, 0),
      };
    default:
      throw new Error('Unknown action');
  }
};

const useInvoicesList = (statuses: MATCHED_STATUS[]): useInvoicesListResult => {
  const { getValue } = useCache((index: number): [string, RequestInit] => {
    return [
      '/invoices.list',
      { body: JSON.stringify({ matched_status: statuses, page: { size: PAGE_SIZE, number: index + 1 } }) },
    ];
  });
  const { data, error, size, setSize, mutate } = useSWRInfinite<InvoicesList>((index) => getValue(index), {
    revalidateAll: true,
  });
  const [actualPage, setActualPage] = useState(1);
  const [state, dispatch] = useReducer(reducer, { loading: false, data: undefined, totalAmount: 0 });

  const loadMore = useCallback(() => {
    dispatch({ type: 'loading' });
    setActualPage(actualPage + 1);
  }, [actualPage]);

  useEffect(() => {
    if (!data || !data[size - 1]) {
      return;
    }

    // If there might be more results, we already fetch the next page to determine whether to show the `load more` action.
    const thereMightBeMoreResults = size === actualPage && data[size - 1].data.length === PAGE_SIZE;
    if (size < actualPage || thereMightBeMoreResults) {
      setSize(size + 1).catch(() => {
        // The hook will handle errors
      });
      return;
    }

    dispatch({ type: 'invoices_loaded', payload: { actualPage, data } });
  }, [actualPage, data, setSize, size]);

  const lastData = data?.[size - 1];
  return {
    ...state,
    error,
    loadMore,
    hasMore: state.loading || !lastData || (size > actualPage && lastData.data.length > 0),
    mutate,
  };
};

export default useInvoicesList;
